/**
 * Conceptos de cobro y generación de cargos (solo Administración).
 *
 * Es la pantalla que convierte «existe un concepto Mensualidad» en «los 40
 * estudiantes deben marzo». Por eso la generación NUNCA es un botón directo:
 * siempre pasa por una vista previa que dice a cuántos afecta, cuánto suma y qué
 * se omite, y solo después se confirma.
 *
 * Requiere conexión: crear obligaciones financieras para toda la institución no es
 * una operación que deba resolverse desde un dispositivo sin validar contra el
 * servidor.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  ConceptAdminDto,
  DeactivationPreviewDto,
  GenerationPreviewDto,
  GenerationResultDto,
} from "@senm/contracts";
import {
  formatMoney,
  MONTH_NAMES,
  money,
  newId,
  parsePositiveAmount,
  toDecimalString,
} from "@senm/domain";
import { ApiRequestError, apiRequest } from "../api/client.js";
import { db, normalizeForSearch, type LocalStudent } from "../local/db.js";
import type { StoredSession } from "../local/session.js";

type Filtro = "ACTIVOS" | "INACTIVOS" | "TODOS";

export function ConceptsPage(props: {
  session: StoredSession;
  conexion: string;
}): JSX.Element {
  const [conceptos, setConceptos] = useState<ConceptAdminDto[]>([]);
  const [filtro, setFiltro] = useState<Filtro>("ACTIVOS");
  const [busqueda, setBusqueda] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [ciclos, setCiclos] = useState<
    {
      id: string;
      name: string;
      closed: boolean;
      isCurrent: boolean;
      startDate: string;
      endDate: string;
    }[]
  >([]);
  const [cicloElegido, setCicloElegido] = useState("");

  // Formulario de alta
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [manejaMeses, setManejaMeses] = useState(true);
  const [monto, setMonto] = useState("");

  // Generación de cargos
  const [generando, setGenerando] = useState<ConceptAdminDto | null>(null);
  const [mesesElegidos, setMesesElegidos] = useState<number[]>([]);
  /**
   * A quién alcanza la generación. Vacío significa TODO el colegio.
   *
   * Existe porque no todo cobro es de todos: recuperación, nivelación, la
   * excursión de un grado. Sin esto había que generar la deuda a la institución
   * entera y anularla uno por uno, y cada anulación es una operación auditada que
   * no debería haber existido nunca.
   */
  /** Grados con sus secciones: alimentan las casillas del alcance. */
  /**
   * Grados a los que aplica el concepto que se está creando.
   *
   * Vacío significa TODOS, y así se explica en pantalla. Existe porque un colegio
   * puede cobrar distinta colegiatura por nivel: sin esto, cada alumno que se
   * inscribe recibe un cargo por CADA concepto mensual, y con tres bandas de
   * precio se le facturaban las tres.
   */
  const [gradosDelConcepto, setGradosDelConcepto] = useState<string[]>([]);
  const [gradosEdicion, setGradosEdicion] = useState<string[]>([]);

  const [grados, setGrados] = useState<
    { id: string; name: string; sections: { id: string; name: string }[] }[]
  >([]);
  const [alcanceGrados, setAlcanceGrados] = useState<string[]>([]);
  const [alcanceSecciones, setAlcanceSecciones] = useState<string[]>([]);
  const [alcanceAlumnos, setAlcanceAlumnos] = useState<LocalStudent[]>([]);
  const [buscaAlumno, setBuscaAlumno] = useState("");
  const [sugerencias, setSugerencias] = useState<LocalStudent[]>([]);
  const [vencimiento, setVencimiento] = useState("");
  const [vistaPrevia, setVistaPrevia] = useState<GenerationPreviewDto | null>(
    null,
  );
  const [confirmando, setConfirmando] = useState(false);

  // Edición
  const [editando, setEditando] = useState<ConceptAdminDto | null>(null);
  const [nombreEdicion, setNombreEdicion] = useState("");
  const [montoEdicion, setMontoEdicion] = useState("");
  const [guardando, setGuardando] = useState(false);

  // Desactivación
  const [desactivando, setDesactivando] = useState<ConceptAdminDto | null>(
    null,
  );
  const [previaBaja, setPreviaBaja] = useState<DeactivationPreviewDto | null>(
    null,
  );
  const [motivo, setMotivo] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const parametros = new URLSearchParams({ estado: filtro });
      if (busqueda.trim()) parametros.set("search", busqueda.trim());
      const datos = await apiRequest<{ concepts: ConceptAdminDto[] }>(
        `/v1/concepts?${parametros}`,
      );
      setConceptos(datos.concepts);
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "Los conceptos de cobro necesitan conexión con el servidor."
          : "No fue posible cargar los conceptos.",
      );
    } finally {
      setCargando(false);
    }
  }, [filtro, busqueda]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Los ciclos se cargan una sola vez: alimentan el selector de generación.
  useEffect(() => {
    void (async () => {
      try {
        const datos = await apiRequest<{
          cycles: {
            id: string;
            name: string;
            closed: boolean;
            isCurrent: boolean;
            startDate: string;
            endDate: string;
          }[];
        }>("/v1/cycles");
        setCiclos(datos.cycles);
        setCicloElegido(
          (previo) =>
            previo ||
            datos.cycles.find((ciclo) => ciclo.isCurrent && !ciclo.closed)
              ?.id ||
            datos.cycles.find((ciclo) => !ciclo.closed)?.id ||
            "",
        );
      } catch {
        // Sin conexión el selector queda vacío y la generación se deshabilita sola.
      }
    })();
  }, []);

  const mensajeDeError = (problema: unknown, respaldo: string): string =>
    problema instanceof ApiRequestError ? problema.message : respaldo;

  const crear = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    if (creando) return;
    setCreando(true);
    setError(null);
    try {
      // El importe se valida con la misma función del dominio que usa el servidor.
      const importe = parsePositiveAmount(monto, props.session.user.currency);
      await apiRequest("/v1/concepts", {
        method: "POST",
        body: {
          name: nombre,
          handlesMonths: manejaMeses,
          amountMinor: importe.minor,
          gradeIds: gradosDelConcepto,
        },
      });
      setAviso(`Concepto "${nombre}" creado. Todavía no genera ninguna deuda.`);
      setNombre("");
      setMonto("");
      setGradosDelConcepto([]);
      await cargar();
    } catch (problema) {
      setError(mensajeDeError(problema, "No fue posible crear el concepto."));
    } finally {
      setCreando(false);
    }
  };

  const abrirEdicion = (concepto: ConceptAdminDto): void => {
    setEditando(concepto);
    setNombreEdicion(concepto.name);
    // Se precarga el monto vigente en el formato que el usuario captura.
    setMontoEdicion(
      toDecimalString(money(concepto.amountMinor, concepto.currency)),
    );
    setGradosEdicion([...concepto.gradeIds]);
    setError(null);
    setAviso(null);
  };

  const guardarEdicion = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    if (editando === null || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const importe = parsePositiveAmount(montoEdicion, editando.currency);
      await apiRequest(`/v1/concepts/${editando.id}`, {
        method: "PATCH",
        body: {
          name: nombreEdicion,
          amountMinor: importe.minor,
          gradeIds: gradosEdicion,
          // Control de concurrencia optimista: si alguien más lo cambió entre
          // que se abrió el formulario y se guardó, el servidor lo rechaza en
          // vez de pisar el cambio ajeno.
          expectedVersion: editando.version,
        },
      });
      setAviso(
        `Concepto "${nombreEdicion}" actualizado. Los cargos ya generados NO cambian: ` +
          "el monto nuevo se usará en las próximas generaciones.",
      );
      setEditando(null);
      await cargar();
    } catch (problema) {
      setError(
        mensajeDeError(problema, "No fue posible actualizar el concepto."),
      );
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Meses que el ciclo cubre de verdad, por su rango de fechas.
   *
   * Un ciclo del 15 de enero al 31 de octubre cubre de enero a octubre: noviembre
   * y diciembre quedan fuera y generar ahí crearía un cargo que no pertenece a
   * ningún periodo lectivo.
   */
  const mesesDelCiclo = (cicloId: string): number[] => {
    const ciclo = ciclos.find((otro) => otro.id === cicloId);
    if (ciclo === undefined) return [];
    const anio = Number(ciclo.startDate.slice(0, 4));
    return Array.from({ length: 12 }, (_, indice) => indice + 1).filter(
      (mes) => {
        const primero = `${anio}-${String(mes).padStart(2, "0")}-01`;
        const ultimo = new Date(Date.UTC(anio, mes, 0))
          .toISOString()
          .slice(0, 10);
        return ultimo >= ciclo.startDate && primero <= ciclo.endDate;
      },
    );
  };

  // Los grados se cargan una vez: no cambian mientras se genera un lote.
  useEffect(() => {
    void (async () => {
      try {
        const datos = await apiRequest<{
          grades: {
            id: string;
            name: string;
            sections: { id: string; name: string }[];
          }[];
        }>("/v1/grades");
        setGrados(datos.grades);
      } catch {
        // Sin grados el alcance por grado no aparece, pero el resto funciona.
      }
    })();
  }, []);

  /** Busca en el almacén local: los estudiantes ya están en el dispositivo. */
  useEffect(() => {
    const texto = buscaAlumno.trim();
    if (texto === "") {
      setSugerencias([]);
      return;
    }
    let vigente = true;
    void (async () => {
      const aguja = normalizeForSearch(texto);
      const todos = await db.students.toArray();
      const yaEstan = new Set(alcanceAlumnos.map((alumno) => alumno.id));
      const encontrados = todos
        .filter(
          (alumno) =>
            !yaEstan.has(alumno.id) &&
            (alumno.searchName.includes(aguja) ||
              alumno.code.toUpperCase().includes(aguja)),
        )
        .slice(0, 8);
      if (vigente) setSugerencias(encontrados);
    })();
    return () => {
      vigente = false;
    };
  }, [buscaAlumno, alcanceAlumnos]);

  /** Alcance en la forma que espera el servidor, o `undefined` si es todo. */
  const alcanceParaEnviar = ():
    | { gradeIds?: string[]; sectionIds?: string[]; studentIds?: string[] }
    | undefined => {
    const scope: {
      gradeIds?: string[];
      sectionIds?: string[];
      studentIds?: string[];
    } = {};
    if (alcanceGrados.length > 0) scope.gradeIds = alcanceGrados;
    if (alcanceSecciones.length > 0) scope.sectionIds = alcanceSecciones;
    if (alcanceAlumnos.length > 0) {
      scope.studentIds = alcanceAlumnos.map((alumno) => alumno.id);
    }
    return Object.keys(scope).length === 0 ? undefined : scope;
  };

  const abrirGeneracion = (concepto: ConceptAdminDto): void => {
    setGenerando(concepto);
    setVistaPrevia(null);
    // Una colegiatura mensual se cobra todos los meses del ciclo: ese es el caso
    // corriente y por tanto el que debe venir hecho. Quitar un mes es una decisión
    // deliberada; olvidarlo no debería ser el estado por omisión.
    setMesesElegidos(concepto.handlesMonths ? mesesDelCiclo(cicloElegido) : []);
    setAlcanceGrados([]);
    setAlcanceSecciones([]);
    setAlcanceAlumnos([]);
    setBuscaAlumno("");
    setVencimiento("");
    setError(null);
  };

  const calcularVistaPrevia = async (): Promise<void> => {
    if (generando === null) return;
    setError(null);
    try {
      const cuerpo: Record<string, unknown> = {
        conceptId: generando.id,
        cycleId: cicloElegido,
      };
      if (generando.handlesMonths) cuerpo.months = mesesElegidos;
      else cuerpo.dueDate = vencimiento;
      const scope = alcanceParaEnviar();
      if (scope !== undefined) cuerpo.scope = scope;

      setVistaPrevia(
        await apiRequest<GenerationPreviewDto>(
          "/v1/concepts/generation/preview",
          {
            method: "POST",
            body: cuerpo,
          },
        ),
      );
    } catch (problema) {
      setError(
        mensajeDeError(problema, "No fue posible calcular la vista previa."),
      );
    }
  };

  const confirmarGeneracion = async (): Promise<void> => {
    if (generando === null || vistaPrevia === null || confirmando) return;
    setConfirmando(true);
    setError(null);
    try {
      const cuerpo: Record<string, unknown> = {
        conceptId: generando.id,
        cycleId: vistaPrevia.cycleId,
        previewToken: vistaPrevia.previewToken,
        // Identificador del lote generado aquí: hace idempotente el reintento.
        batchId: newId(),
      };
      const scopeConfirmado = alcanceParaEnviar();
      if (scopeConfirmado !== undefined) cuerpo.scope = scopeConfirmado;
      if (generando.handlesMonths) cuerpo.months = mesesElegidos;
      else cuerpo.dueDate = vencimiento;

      const resultado = await apiRequest<GenerationResultDto>(
        "/v1/concepts/generation/confirm",
        {
          method: "POST",
          body: cuerpo,
        },
      );
      setAviso(
        `Se crearon ${resultado.chargesCreated} cargo(s) por ${formatMoney(
          money(resultado.totalAmountMinor, resultado.currency),
        )}` +
          (resultado.chargesSkipped > 0
            ? `. Se omitieron ${resultado.chargesSkipped} que ya existían.`
            : "."),
      );
      setGenerando(null);
      setVistaPrevia(null);
      await cargar();
    } catch (problema) {
      setError(mensajeDeError(problema, "No fue posible generar los cargos."));
      // La vista previa dejó de ser válida: se obliga a recalcularla.
      setVistaPrevia(null);
    } finally {
      setConfirmando(false);
    }
  };

  const abrirBaja = async (concepto: ConceptAdminDto): Promise<void> => {
    setDesactivando(concepto);
    setMotivo("");
    setError(null);
    try {
      setPreviaBaja(
        await apiRequest<DeactivationPreviewDto>(
          `/v1/concepts/${concepto.id}/deactivation-preview`,
        ),
      );
    } catch (problema) {
      setError(mensajeDeError(problema, "No fue posible calcular el impacto."));
    }
  };

  const confirmarBaja = async (): Promise<void> => {
    if (desactivando === null || previaBaja === null) return;
    setError(null);
    try {
      await apiRequest(`/v1/concepts/${desactivando.id}/deactivate`, {
        method: "POST",
        body: {
          previewToken: previaBaja.previewToken,
          operationId: newId(),
          reason: motivo,
          expectedVersion: desactivando.version,
        },
      });
      setAviso(
        `Concepto "${desactivando.name}" desactivado. Los pagos y recibos no se tocaron.`,
      );
      setDesactivando(null);
      setPreviaBaja(null);
      await cargar();
    } catch (problema) {
      setError(
        mensajeDeError(problema, "No fue posible desactivar el concepto."),
      );
    }
  };

  const activar = async (concepto: ConceptAdminDto): Promise<void> => {
    setError(null);
    try {
      await apiRequest(`/v1/concepts/${concepto.id}/activate`, {
        method: "POST",
      });
      setAviso(
        `Concepto "${concepto.name}" reactivado. Los cargos cancelados no se recuperan.`,
      );
      await cargar();
    } catch (problema) {
      setError(
        mensajeDeError(problema, "No fue posible reactivar el concepto."),
      );
    }
  };

  const alternarMes = (mes: number): void => {
    setVistaPrevia(null); // cualquier cambio invalida la vista previa
    setMesesElegidos((previo) =>
      previo.includes(mes)
        ? previo.filter((m) => m !== mes)
        : [...previo, mes].sort((a, b) => a - b),
    );
  };

  return (
    <section aria-labelledby="titulo-conceptos">
      <h1 id="titulo-conceptos">Conceptos de cobro</h1>
      <p className="ayuda">
        Un concepto es una plantilla reutilizable. Guardarlo{" "}
        <strong>no crea ninguna deuda</strong>: para eso está «Generar cargos».
      </p>

      {aviso !== null && (
        <div className="aviso-caja ok" role="status">
          {aviso}
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      {/* --- Alta -------------------------------------------------------- */}
      <form className="tarjeta" onSubmit={(evento) => void crear(evento)}>
        <h2>Nuevo concepto</h2>
        <div className="campos">
          <label>
            Nombre
            <input
              type="text"
              value={nombre}
              onChange={(evento) => setNombre(evento.target.value)}
              required
              minLength={3}
              maxLength={80}
            />
          </label>
          <label>
            Monto ({props.session.user.currency})
            <input
              type="text"
              inputMode="decimal"
              value={monto}
              onChange={(evento) => setMonto(evento.target.value)}
              placeholder="400.00"
              required
            />
          </label>
          <label className="casilla">
            <input
              type="checkbox"
              checked={manejaMeses}
              onChange={(evento) => setManejaMeses(evento.target.checked)}
            />
            Se cobra por mes (mensualidad)
          </label>
        </div>
        <p className="ayuda">
          {manejaMeses
            ? "Al generar cargos podrá elegir uno o varios meses. Generará mora del 1.5 % diario desde el día 1 del mes siguiente al vencimiento."
            : "Se cobra una sola vez. Al generar cargos indicará una fecha de vencimiento y no generará mora."}
        </p>

        <AlcancePorGrado
          grados={grados}
          elegidos={gradosDelConcepto}
          onCambio={setGradosDelConcepto}
        />

        <button type="submit" className="boton" disabled={creando}>
          {creando ? "Creando…" : "Crear concepto"}
        </button>
      </form>

      {/* --- Listado ----------------------------------------------------- */}
      <div className="filtros">
        <label>
          Buscar
          <input
            type="search"
            value={busqueda}
            onChange={(evento) => setBusqueda(evento.target.value)}
            placeholder="Nombre del concepto"
          />
        </label>
        <label>
          Estado
          <select
            value={filtro}
            onChange={(evento) => setFiltro(evento.target.value as Filtro)}
          >
            <option value="ACTIVOS">Activos</option>
            <option value="INACTIVOS">Inactivos</option>
            <option value="TODOS">Todos</option>
          </select>
        </label>
      </div>

      {cargando && <p role="status">Cargando conceptos…</p>}

      <table className="tabla">
        <caption className="oculto-visual">
          Conceptos de cobro de la institución
        </caption>
        <thead>
          <tr>
            <th scope="col">Concepto</th>
            <th scope="col">Tipo</th>
            <th scope="col">Monto vigente</th>
            <th scope="col">Cargos vigentes</th>
            <th scope="col">Estado</th>
            <th scope="col">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {conceptos.map((concepto) => (
            <tr key={concepto.id}>
              <th scope="row">{concepto.name}</th>
              <td>{concepto.handlesMonths ? "Mensual" : "Cargo suelto"}</td>
              <td>
                {formatMoney(money(concepto.amountMinor, concepto.currency))}
              </td>
              <td>{concepto.activeChargeCount}</td>
              <td>{concepto.active ? "✓ Activo" : "✕ Inactivo"}</td>
              <td className="acciones">
                {concepto.active ? (
                  <>
                    <button
                      type="button"
                      className="boton"
                      onClick={() => abrirGeneracion(concepto)}
                    >
                      Generar cargos
                    </button>
                    <button
                      type="button"
                      onClick={() => abrirEdicion(concepto)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="boton peligro"
                      onClick={() => void abrirBaja(concepto)}
                    >
                      Desactivar
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => void activar(concepto)}>
                    Reactivar
                  </button>
                )}
              </td>
            </tr>
          ))}
          {conceptos.length === 0 && !cargando && (
            <tr>
              <td colSpan={6}>No hay conceptos con ese filtro.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* --- Edición del concepto ---------------------------------------- */}
      {editando !== null && (
        <div className="tarjeta" role="dialog" aria-labelledby="titulo-editar">
          <h2 id="titulo-editar">Editar concepto — {editando.name}</h2>

          <p className="ayuda">
            Cambiar el monto{" "}
            <strong>no modifica ningún cargo ya generado</strong>. Cada cargo
            guarda el importe con el que nació, para que un recibo emitido siga
            cuadrando con lo que se cobró. El monto nuevo se usará en las
            próximas generaciones, y el cambio queda registrado con fecha y
            responsable.
          </p>

          <form onSubmit={(evento) => void guardarEdicion(evento)}>
            <label htmlFor="editar-nombre">Nombre</label>
            <input
              id="editar-nombre"
              value={nombreEdicion}
              onChange={(evento) => setNombreEdicion(evento.target.value)}
              minLength={3}
              maxLength={80}
              required
            />

            <label htmlFor="editar-monto">Monto ({editando.currency})</label>
            <input
              id="editar-monto"
              inputMode="decimal"
              value={montoEdicion}
              onChange={(evento) => setMontoEdicion(evento.target.value)}
              required
            />

            {editando.handlesMonths && (
              <AlcancePorGrado
                grados={grados}
                elegidos={gradosEdicion}
                onCambio={setGradosEdicion}
              />
            )}

            {editando.activeChargeCount > 0 && (
              <p className="aviso">
                Este concepto tiene {editando.activeChargeCount} cargo(s)
                vigente(s). Seguirán cobrándose por su monto original.
              </p>
            )}

            <div className="acciones">
              <button
                type="submit"
                className="boton primario"
                disabled={guardando}
              >
                {guardando ? "Guardando…" : "Guardar cambios"}
              </button>
              <button type="button" onClick={() => setEditando(null)}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* --- Generación de cargos ---------------------------------------- */}
      {generando !== null && (
        <div className="tarjeta" role="dialog" aria-labelledby="titulo-generar">
          <h2 id="titulo-generar">Generar cargos — {generando.name}</h2>

          <label>
            Ciclo escolar
            <select
              value={cicloElegido}
              onChange={(evento) => {
                setCicloElegido(evento.target.value);
                setVistaPrevia(null);
                // Cada ciclo cubre sus propios meses: mantener la marca del anterior
                // dejaría seleccionado un mes que este ciclo ni siquiera abarca.
                if (generando?.handlesMonths === true) {
                  setMesesElegidos(mesesDelCiclo(evento.target.value));
                }
              }}
            >
              {ciclos
                .filter((ciclo) => !ciclo.closed)
                .map((ciclo) => (
                  <option key={ciclo.id} value={ciclo.id}>
                    {ciclo.name}
                  </option>
                ))}
            </select>
          </label>

          {generando.handlesMonths ? (
            <fieldset>
              <legend>Meses a generar</legend>
              <p className="ayuda">
                Vienen marcados los meses que cubre el ciclo. Los de fuera no se
                pueden generar: crearían un cargo que no pertenece a ningún
                periodo lectivo.
              </p>
              <div className="meses">
                {MONTH_NAMES.map((nombreMes, indice) => {
                  const dentro = mesesDelCiclo(cicloElegido).includes(
                    indice + 1,
                  );
                  return (
                    <label key={nombreMes} className="casilla">
                      <input
                        type="checkbox"
                        checked={mesesElegidos.includes(indice + 1)}
                        onChange={() => alternarMes(indice + 1)}
                        disabled={!dentro}
                      />
                      {nombreMes}
                      {!dentro && (
                        <span className="oculto-visual">
                          {" "}
                          (fuera del ciclo)
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : (
            <label>
              Fecha de vencimiento
              <input
                type="date"
                value={vencimiento}
                onChange={(evento) => {
                  setVencimiento(evento.target.value);
                  setVistaPrevia(null);
                }}
                required
              />
            </label>
          )}

          <fieldset className="modos">
            <legend>A quién se le genera</legend>
            <p className="ayuda">
              {alcanceGrados.length === 0 &&
              alcanceSecciones.length === 0 &&
              alcanceAlumnos.length === 0
                ? "Sin elegir nada, el cargo se genera para TODO el colegio."
                : "Solo a lo marcado. Los tres filtros se suman entre sí."}
            </p>

            <div className="fichas-seccion">
              {grados.map((grado) => (
                <label key={grado.id} className="ficha-seccion">
                  <input
                    type="checkbox"
                    checked={alcanceGrados.includes(grado.id)}
                    onChange={() => {
                      setVistaPrevia(null);
                      setAlcanceGrados((previos) =>
                        previos.includes(grado.id)
                          ? previos.filter((otro) => otro !== grado.id)
                          : [...previos, grado.id],
                      );
                    }}
                  />
                  {grado.name}
                </label>
              ))}
            </div>

            {grados.some((grado) => grado.sections.length > 0) && (
              <div className="fichas-seccion">
                {grados.flatMap((grado) =>
                  grado.sections.map((seccion) => (
                    <label key={seccion.id} className="ficha-seccion">
                      <input
                        type="checkbox"
                        checked={alcanceSecciones.includes(seccion.id)}
                        onChange={() => {
                          setVistaPrevia(null);
                          setAlcanceSecciones((previas) =>
                            previas.includes(seccion.id)
                              ? previas.filter((otra) => otra !== seccion.id)
                              : [...previas, seccion.id],
                          );
                        }}
                      />
                      {grado.name} {seccion.name}
                    </label>
                  )),
                )}
              </div>
            )}

            {alcanceAlumnos.length > 0 && (
              <ul className="fichas-seccion">
                {alcanceAlumnos.map((alumno) => (
                  <li key={alumno.id} className="ficha-seccion">
                    <span>
                      {alumno.code} · {alumno.fullName}
                    </span>
                    <button
                      type="button"
                      className="boton compacto"
                      onClick={() => {
                        setVistaPrevia(null);
                        setAlcanceAlumnos((previos) =>
                          previos.filter((otro) => otro.id !== alumno.id),
                        );
                      }}
                    >
                      Quitar
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label>
              <span>Añadir estudiante suelto por código o nombre</span>
              <input
                type="search"
                value={buscaAlumno}
                onChange={(evento) => setBuscaAlumno(evento.target.value)}
                placeholder="Código o nombre"
              />
            </label>

            {sugerencias.length > 0 && (
              <ul className="fichas-seccion">
                {sugerencias.map((alumno) => (
                  <li key={alumno.id}>
                    <button
                      type="button"
                      className="boton compacto"
                      onClick={() => {
                        setVistaPrevia(null);
                        setAlcanceAlumnos((previos) => [...previos, alumno]);
                        setBuscaAlumno("");
                      }}
                    >
                      + {alumno.code} · {alumno.fullName}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </fieldset>

          <p>
            Monto que se usará:{" "}
            <strong>
              {formatMoney(money(generando.amountMinor, generando.currency))}
            </strong>
          </p>

          <div className="acciones">
            <button
              type="button"
              onClick={() => void calcularVistaPrevia()}
              disabled={
                cicloElegido === "" ||
                (generando.handlesMonths
                  ? mesesElegidos.length === 0
                  : vencimiento === "")
              }
            >
              Calcular vista previa
            </button>
            <button type="button" onClick={() => setGenerando(null)}>
              Cancelar
            </button>
          </div>

          {vistaPrevia !== null && (
            <div className="vista-previa">
              <h3>Vista previa</h3>
              <dl className="resumen">
                <dt>Ciclo</dt>
                <dd>{vistaPrevia.cycleName}</dd>
                <dt>Estudiantes activos incluidos</dt>
                <dd>{vistaPrevia.studentsIncluded}</dd>
                <dt>Cargos que se crearán</dt>
                <dd>{vistaPrevia.chargesToCreate}</dd>
                <dt>Cargos omitidos (ya existen)</dt>
                <dd>{vistaPrevia.chargesSkipped}</dd>
                <dt>Total del lote</dt>
                <dd>
                  <strong>
                    {formatMoney(
                      money(vistaPrevia.totalAmountMinor, vistaPrevia.currency),
                    )}
                  </strong>
                </dd>
              </dl>

              <div className="aviso-caja" role="alert">
                <strong>Atención.</strong> {vistaPrevia.warning}
              </div>

              <details>
                <summary>
                  Ver los {vistaPrevia.students.length} estudiantes incluidos
                </summary>
                <ul className="lista-simple">
                  {vistaPrevia.students.map((estudiante) => (
                    <li key={estudiante.studentId}>
                      {estudiante.code} — {estudiante.fullName}
                      {estudiante.monthsSkipped.length > 0 && (
                        <span className="ayuda">
                          {" "}
                          (se omiten {estudiante.monthsSkipped.length})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </details>

              <button
                type="button"
                className="boton"
                onClick={() => void confirmarGeneracion()}
                disabled={confirmando || vistaPrevia.chargesToCreate === 0}
              >
                {confirmando
                  ? "Generando…"
                  : `Sí, generar ${vistaPrevia.chargesToCreate} cargo(s)`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* --- Desactivación ----------------------------------------------- */}
      {desactivando !== null && previaBaja !== null && (
        <div className="tarjeta" role="dialog" aria-labelledby="titulo-baja">
          <h2 id="titulo-baja">Desactivar — {desactivando.name}</h2>
          <dl className="resumen">
            <dt>Cargos que se cancelarán</dt>
            <dd>{previaBaja.chargesToCancel}</dd>
            <dt>De ellos, con abono parcial</dt>
            <dd>{previaBaja.chargesPartiallyPaid}</dd>
            <dt>Cargos ya pagados (no se tocan)</dt>
            <dd>{previaBaja.chargesPaid}</dd>
            <dt>Capital que se cancela</dt>
            <dd>
              {formatMoney(
                money(previaBaja.capitalToCancelMinor, previaBaja.currency),
              )}
            </dd>
            <dt>Mora que se cancela</dt>
            <dd>
              {formatMoney(
                money(previaBaja.lateFeeToCancelMinor, previaBaja.currency),
              )}
            </dd>
            <dt>Estudiantes afectados</dt>
            <dd>{previaBaja.studentsAffected}</dd>
          </dl>

          <div className="aviso-caja" role="alert">
            {previaBaja.warning}
          </div>

          <label>
            Motivo (queda en la auditoría)
            <input
              type="text"
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              minLength={5}
              maxLength={300}
              required
            />
          </label>

          <div className="acciones">
            <button
              type="button"
              className="boton"
              onClick={() => void confirmarBaja()}
              disabled={motivo.trim().length < 5}
            >
              Sí, desactivar y cancelar pendientes
            </button>
            <button
              type="button"
              onClick={() => {
                setDesactivando(null);
                setPreviaBaja(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * A qué grados aplica un concepto.
 *
 * Vacío significa TODOS, y se dice en pantalla: es lo que corresponde al colegio
 * que cobra una sola colegiatura, y no tiene por qué configurar nada.
 *
 * Importa marcarlo bien. La generación automática que corre al inscribir a un
 * alumno crea un cargo por cada concepto mensual que le aplique; un colegio con
 * tres precios de colegiatura y ningún grado marcado le genera las tres.
 */
function AlcancePorGrado(props: {
  grados: { id: string; name: string }[];
  elegidos: string[];
  onCambio: (grados: string[]) => void;
}): JSX.Element {
  if (props.grados.length === 0) {
    return (
      <p className="ayuda">
        Todavía no hay grados. El concepto aplicará a todos los que se creen.
      </p>
    );
  }

  const alternar = (id: string): void => {
    props.onCambio(
      props.elegidos.includes(id)
        ? props.elegidos.filter((otro) => otro !== id)
        : [...props.elegidos, id],
    );
  };

  return (
    <fieldset className="alcance-grados">
      <legend>¿A qué grados aplica?</legend>
      <p className="ayuda">
        {props.elegidos.length === 0
          ? "Sin marcar ninguno, aplica a TODOS los grados."
          : `Aplica solo a ${props.elegidos.length} grado(s).`}
      </p>
      <div className="casillas-grado">
        {props.grados.map((grado) => (
          <label key={grado.id} className="casilla">
            <input
              type="checkbox"
              checked={props.elegidos.includes(grado.id)}
              onChange={() => alternar(grado.id)}
            />
            {grado.name}
          </label>
        ))}
      </div>
      {props.elegidos.length > 0 && (
        <button
          type="button"
          className="boton compacto"
          onClick={() => props.onCambio([])}
        >
          Quitar todos (aplicar a todos los grados)
        </button>
      )}
    </fieldset>
  );
}
